import { datadogRum } from '@datadog/browser-rum';
import { useSetAtom } from 'jotai';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IconSvg } from '@/lib/v2/components';
import { CardAction } from '@/lib/v2/organisms/CardAction';
import { CardActionProps } from '@/lib/v2/organisms/CardAction/CardAction';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';

import { atomRuleConfigCreation } from '@/modules/CampaignsModule/atoms/createActionsAtom';
import { TRANSLATE_RULES_TYPES } from '@/modules/CampaignsModule/constants';
import { atomToggleCreateRuleModal } from '@/modules/RulesModule/atoms/rules';
import { ACTION_RULE_TYPE } from '@/modules/RulesModule/constants';
import {
  RulesAPIIcon,
  RulesDateIcon,
  RulesExternalURLIcon,
  RulesFlowIcon,
  RulesFormIcon,
  RulesTagAssociatedIcon,
} from '@/modules/RulesModule/images/icons';
import { RuleTypes } from '@/modules/RulesModule/interfaces';

interface RulesTabPaneContainerProps {
  onClose?: () => void;
}

const RulesTabPaneContainer = ({ onClose }: RulesTabPaneContainerProps) => {
  const { t } = useTranslation();
  const showNewAutomationModule = useFeatureFlag('showNewAutomationModule');

  const setToggleCreateRuleModal = useSetAtom(atomToggleCreateRuleModal);
  const setRuleConfigCreation = useSetAtom(atomRuleConfigCreation);

  const handleCreateRulesModal = useCallback(
    (ruleType: RuleTypes) => {
      setRuleConfigCreation({ actionType: ACTION_RULE_TYPE, ruleType });
      setToggleCreateRuleModal(true);
      datadogRum.addAction('campaigns.onCreateRule', {
        ruleType,
      });
      onClose?.();
    },
    [setToggleCreateRuleModal, onClose, setRuleConfigCreation]
  );

  const cardActionsList = useMemo<CardActionProps[]>(() => {
    const list = [
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.rulesDate.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.rulesDate.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'rules-date',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesDateIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.DATE),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.tagAssociated.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.tagAssociated.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'tag-associated',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesTagAssociatedIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.TAG),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.externalURL.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.externalURL.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'external-url',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesExternalURLIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.EXTERNAL_CALL),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromAPI.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromAPI.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'from-api',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesAPIIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.API_INTEGRATOR),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromForm.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.fromForm.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'from-form',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesFormIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.WEB_FORM),
      },
      {
        description: t(`${TRANSLATE_RULES_TYPES}.TYPES.flowTemplate.description`),
        title: t(`${TRANSLATE_RULES_TYPES}.TYPES.flowTemplate.title`),
        buttonText: t('ALL_CARDS_ACTIONS.simpleEmail.buttonText'),
        id: 'flow-template',
        icon: (
          <IconSvg
            rounded
            bgColor="headingIcon"
            height="56px"
            svgComponent={<RulesFlowIcon />}
            width="56px"
            withPadding="medium"
          />
        ),
        onClickAction: () => handleCreateRulesModal(RuleTypes.FLOW_TEMPLATE),
      },
    ];

    if (showNewAutomationModule) {
      return list.filter((x) => x.id !== 'flow-template');
    }
    return list;
  }, [t, handleCreateRulesModal, showNewAutomationModule]);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {cardActionsList.map((cardAction) => {
        return <CardAction key={cardAction.id} {...cardAction} justifyActionButton="end" />;
      })}
    </div>
  );
};

export default memo(RulesTabPaneContainer);
